/**
Copyright (C) 2022 by SeQuenX  - All Rights Reserved
This file is part of the ComplyVantage product development,
and is released under the "Commercial License Agreement".
You should have received a copy of the Commercial License Agreement license with
this file. If not, please write to: legal@sequenx.com, or visit www.sequenx.com
*/
import React from "react";
import { motion } from "framer-motion";
import { info_security_policy } from "../assets/svg";

const ServiceDetailItem = ({ title, description }) => (
  <motion.li
    initial={{ opacity: 0, y: 10 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.4 }}
    viewport={{ once: true }}
    className="text-gray-700 leading-8 mb-4"
  >
    {title}
    {description && (
      <p className="text-sm text-gray-500 font-normal">{description}</p>
    )}
  </motion.li>
);

const InfoSecurityPolicyDetails = () => {
  const securityMeasures = [
    {
      title:
        "Assigning dedicated personnel and allocating budget to security management.",
    },
    {
      title:
        "Implementing appropriate security technology and high-availability, recoverable systems and facilities.",
    },
    {
      title:
        "Continually evaluating and improving procedures related to security.",
    },
    {
      title:
        "Adopting and enforcing requisite policies and ensuring that employees are kept aware of the ISMS and their responsibilities towards it via communication and training programmes.",
    },
    {
      title:
        "Striving to maintain compliance with all applicable legal and industry requirements.",
    },
  ];

  return (
    <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h1 className="text-4xl font-bold text-gray-800 mb-4 ">
            Information Security Policy
          </h1>
          <div className="w-24 h-1 bg-blue-600 mx-auto"></div>
        </motion.div>

        <div className="flex flex-col md:flex-row items-center gap-12">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
            className="w-full md:w-1/2"
          >
            <img
              src={info_security_policy}
              alt="Information Security Policy"
              className="w-full max-w-lg mx-auto rounded-lg shadow-xl transform hover:scale-105 transition-transform duration-300"
            />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            viewport={{ once: true }}
            className="w-full md:w-1/2 space-y-6"
          >
            <div className="prose prose-lg">
              <p className="text-gray-700 leading-relaxed">
                The group of affiliated SeQuenX companies ("SeQuenX Companies")
                have controls to protect the confidentiality, integrity, and
                availability of information that is owned by or entrusted to
                them. The intent of this document is to provide assurances to
                customers, potential customers, and any other interested parties
                that information in our companies custody is properly protected
                - and that the protections in place are consistent with
                appropriate compliance requirements.
              </p>
              <h2 className="text-2xl font-bold text-gray-800 my-6">
                Overview
              </h2>
              <p className="text-gray-700 leading-relaxed">
                SeQuenX Companies provide software, consulting, and online
                services. The Boards of Directors and management of SeQuenX
                Companies are committed to preserving the confidentiality,
                integrity and availability of all the physical and electronic
                information assets throughout their organisation to maintain
                legal, regulatory and contractual compliance and to safeguard
                business integrity and commercial reputation.
              </p>
            </div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          viewport={{ once: true }}
          className="mt-20"
        >
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="prose prose-lg max-w-none">
              <p className="text-gray-700 leading-relaxed">
                To achieve this, SeQuenX Companies have implemented a Group-wide
                Information Security Management System (ISMS) in accordance with
                the international standard ISO/IEC 27001:2013 requirements. The
                ISMS is subject to continuous systematic review and improvement.
              </p>
              <p className="text-gray-700 leading-relaxed mt-4">
                In accordance with the ISMS, SeQuenX Companies demonstrate their
                commitment to information security by:
              </p>
              <ul className="list-disc list-inside">
                {securityMeasures.map((detail, index) => (
                  <ServiceDetailItem key={index} title={detail.title} />
                ))}
              </ul>

              <h2 className="text-2xl font-bold text-gray-800 my-6">Staff</h2>
              <p className="text-gray-700 leading-relaxed">
                SeQuenX Companies employ staff whose responsibility is the
                protection of information. In addition, it is the responsibility
                of all of employees to be aware of information security issues
                within their daily work. To promote awareness, employees of
                SeQuenX Companies are provided with training on topics such as
                the companys security policies, their responsibilities to
                protect the confidentiality of information entrusted to them,
                the appropriate use of resources, the extra care required for
                the protection of mobile devices, and other related topics.
              </p>

              <h2 className="text-2xl font-bold text-gray-800 my-6">
                Confidentiality Agreements
              </h2>
              <p className="text-gray-700 leading-relaxed">
                SeQuenX Companies enter into confidentiality or non-disclosure
                agreements with their vendors, contractors, employees and
                clients to contractually safeguard personal and other
                confidential information belonging to a SeQuenX company or in
                the custody of a SeQuenX company.
              </p>

              <h2 className="text-2xl font-bold text-gray-800 my-6">
                Audits and Assessments
              </h2>
              <p className="text-gray-700 leading-relaxed">
                Regular risk assessments are performed to help SeQuenX Companies
                identify any potential risks to their information assets and to
                help prioritize efforts to mitigate those risks. Periodically,
                companies also engage external firms to perform more in-depth
                evaluations of their security controls by conducting penetration
                testing and other similar exercises. In addition to external
                reviews, internal tests are conducted on a regular basis to
                ensure compliance and verify control effectiveness.
                Vulnerability scans are conducted, and the results of these
                scans are used to identify vulnerabilities to be addressed and
                to prioritize the efforts of those staff that are responsible
                for keeping the IT systems of SeQuenX Companies up to date and
                protected.
              </p>

              <h2 className="text-2xl font-bold text-gray-800 my-6">
                Physical Security
              </h2>
              <p className="text-gray-700 leading-relaxed">
                All sites hosting information belonging to a SeQuenX company (or
                information that is managed by a SeQuenX company on the behalf
                of others) are secured. Such facilities are protected by
                physical security barriers and entry controls designed to
                prevent unauthorized access, damage, and interference. Fire
                suppression, environmental controls, and uninterrupted power
                supplies are all in place, as are security cameras to monitor
                the facilities and all entrances to them.
              </p>

              <h2 className="text-2xl font-bold text-gray-800 my-6">
                Operational Security
              </h2>
              <p className="text-gray-700 leading-relaxed">
                Responsibilities and procedures for the management and operation
                of information processing facilities are established and
                separation of duties by function have been implemented across
                the companies that comprise the SeQuenX. Operational change to
                systems is controlled through various defined change management
                processes.
              </p>

              <h2 className="text-2xl font-bold text-gray-800 my-6">
                Access Control
              </h2>
              <p className="text-gray-700 leading-relaxed">
                Access to information, information processing facilities, and
                business processes are controlled on the basis of business and
                security requirements. Access control rules take into account
                the basic principle of "need-to-know" and the sensitivity of
                corporate and personal information.
              </p>
              <p className="text-gray-700 leading-relaxed mt-4">
                Layers of security controls limit access to information. These
                include controls at the network, application, operating system,
                and database levels. Passwords are used in conjunction with each
                of these layers; they are subject to defined password
                construction rules and must be changed at regular intervals.
                Password administration and management are controlled processes
                that generate automated audit records.
              </p>

              <h2 className="text-2xl font-bold text-gray-800 my-6">
                Data Communications Security
              </h2>
              <p className="text-gray-700 leading-relaxed">
                Technologies such as SSL (TLS), and IPsec are used to encrypt
                data when in transit over public networks. The use of such
                technologies is dependent upon the level of sensitivity of the
                information, both corporate and personal.
              </p>

              <h2 className="text-2xl font-bold text-gray-800 my-6">
                Computer Security Measures
              </h2>
              <p className="text-gray-700 leading-relaxed">
                Various security technologies are deployed within the
                infrastructures and include firewalls, anti-virus, antispyware,
                encryption, and intrusion detection systems and processes.
              </p>
              <p className="text-gray-700 leading-relaxed mt-4">
                Security data is logged and regularly reviewed to identify
                policy violations and security incidents. Incidents are
                documented and investigated to determine severity, root cause,
                and follow-up actions required. Measures to be taken to prevent
                re-occurrence are also identified, documented, and implemented
                as needed.
              </p>

              <h2 className="text-2xl font-bold text-gray-800 my-6">
                Disaster Prevention and Recovery
              </h2>
              <p className="text-gray-700 leading-relaxed">
                Adequate back-up capabilities exist to ensure that all essential
                information and software can be recovered following a disaster
                or media failure. Backup information is stored at a remote
                secure location, at a sufficient distance to escape any damage
                from a disaster at the primary site. Backup media is protected
                against unauthorized access, misuse or corruption during
                transportation beyond the data center boundaries.
              </p>
              <p className="text-gray-700 leading-relaxed mt-4">
                Combinations of preventive and recovery controls are implemented
                to help protect from harm due to loss of data or processing
                capabilities. These controls are designed based on an assessment
                of risk and are meant to keep the harmful effects of any outages
                to a minimum. The processes making up these control measures are
                tested on a regular basis.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default InfoSecurityPolicyDetails;
